import { useEffect, useRef } from 'react';
import { ArchiveRestore, Trash2, X } from 'lucide-react';
import { Permissao, formatarDataHora, type ProcessoLixeiraDto } from '@nexital/shared';
import { useQuadro } from '../../application/dependencias';
import { useConfirmar } from '../design-system/Confirmacao';

const VAZIA: ProcessoLixeiraDto[] = [];

const AVISO_RECUPERACAO = 'Some da tela para todos; os dados e os arquivos continuam guardados.';

function ItemLixeira({ processo: p, podeExcluir }: { processo: ProcessoLixeiraDto; podeExcluir: boolean }) {
  const restaurar = useQuadro((s) => s.restaurar);
  const excluir = useQuadro((s) => s.excluirDaLixeira);
  const confirmar = useConfirmar();
  const excluirComConfirmacao = async () => {
    if (await confirmar({ titulo: 'Excluir da Lixeira?', mensagem: `${p.numero} será excluído. ${AVISO_RECUPERACAO}`, rotulo: 'Excluir' })) {
      void excluir([p.uuid]);
    }
  };
  return (
    <li className="painel-lixeira__item">
      <div>
        <strong>{p.numero}</strong>
        <span>{p.objeto}</span>
        <small>Enviado em {formatarDataHora(p.entrouEm)}</small>
      </div>
      <div className="painel-lixeira__acoes">
        <button type="button" className="botao botao--secundario" onClick={() => void restaurar(p.uuid)} aria-label={`Restaurar ${p.numero}`}>
          <ArchiveRestore size={16} aria-hidden /> Restaurar
        </button>
        {podeExcluir && (
          <button type="button" className="botao botao--secundario" onClick={() => void excluirComConfirmacao()} aria-label={`Excluir ${p.numero}`}>
            <Trash2 size={16} aria-hidden /> Excluir
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * Painel aberto pelo ícone da Lixeira: processos que estão nela, a ação Restaurar (RQ-EST-010) e, para quem pode
 * esvaziar a Lixeira, Excluir em cada item e Esvaziar Lixeira (RQ-EST-012). O motivo gravado é o do envio à Lixeira.
 */
export function PainelLixeira({ fechar }: { fechar: () => void }) {
  const itens = useQuadro((s) => s.dados?.lixeira ?? VAZIA);
  const excluir = useQuadro((s) => s.excluirDaLixeira);
  const podeEsvaziar = useQuadro((s) => s.dados?.permissoes.includes(Permissao.EsvaziarLixeira) ?? false);
  const confirmar = useConfirmar();
  const titulo = useRef<HTMLHeadingElement>(null);

  useEffect(() => titulo.current?.focus(), []);

  const esvaziar = async () => {
    const quantos = itens.length === 1 ? '1 processo será excluído' : `${itens.length} processos serão excluídos`;
    if (await confirmar({ titulo: 'Esvaziar a Lixeira?', mensagem: `${quantos}. ${AVISO_RECUPERACAO}`, rotulo: 'Esvaziar Lixeira' })) {
      void excluir(itens.map((p) => p.uuid));
    }
  };

  return (
    <section
      id="painel-lixeira"
      className="painel-lixeira"
      aria-labelledby="painel-lixeira-titulo"
      onKeyDown={(e) => e.key === 'Escape' && fechar()}
    >
      <header className="painel-lixeira__cabecalho">
        <h2 id="painel-lixeira-titulo" ref={titulo} tabIndex={-1}>
          Lixeira <span className="grupo__contador lixeira__total">{itens.length}</span>
        </h2>
        <div className="painel-lixeira__acoes">
          {podeEsvaziar && itens.length > 0 && (
            <button type="button" className="botao botao--perigo" onClick={() => void esvaziar()}>
              <Trash2 size={16} aria-hidden /> Esvaziar Lixeira
            </button>
          )}
          <button type="button" className="painel-lixeira__fechar" onClick={fechar} aria-label="Fechar a Lixeira">
            <X size={18} aria-hidden />
          </button>
        </div>
      </header>
      {itens.length === 0 && <p className="painel-lixeira__vazio">A Lixeira está vazia.</p>}
      <ul className="painel-lixeira__lista">
        {itens.map((p) => (
          <ItemLixeira key={p.uuid} processo={p} podeExcluir={podeEsvaziar} />
        ))}
      </ul>
    </section>
  );
}
